import React, { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { SUPPORTED_CURRENCIES } from '@/constants';
import { useTranslation } from 'react-i18next';
import { SelectWithAlert } from '@/components/ui/select-with-alert';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { AlertTitle, Alert } from '@/components/ui/alert';
import { AlertCircle } from "lucide-react";
import { Button } from '@/components/ui/button';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import { NOT_SPECIFIED, getSwiftCodeForBank } from '@/bank_constants';

const SignupBankAccount = ({ bankAccounts, setBankAccounts, onValidationChange, inputChecked }) => {
  const { t } = useTranslation();
  const [error, setError] = useState();

  const isIbanAlreadyExists = (iban, accountId) => {
    if (!iban || iban.length === 0) return false;
    const normalizedIban = electronicFormatIBAN(iban);
    return bankAccounts.some((account) => 
      account.account && 
      electronicFormatIBAN(account.account) === normalizedIban && 
      account.id !== accountId
    );
  };

  // Function to check if any account has validation errors
  const hasValidationErrors = () => {
    if (bankAccounts.length === 0) return true;
    
    return bankAccounts.some(account => {
      // Check if account has IBAN
      if (!account.account || account.account.length === 0) return true;
      
      // Check IBAN validity
      const isValid = isValidIBAN(electronicFormatIBAN(account.account));
      if (!isValid) return true;
      
      // Check for duplicate IBAN
      if (isIbanAlreadyExists(account.account, account.id)) return true;
      
      return false;
    });
  };

  // Expose validation function to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasValidationErrors());
    }
  }, [bankAccounts, error, onValidationChange]);

  const handleAddAccount = () => {
    const newId = Date.now();
    const defaultCurrency = 'PLN';
    
    setBankAccounts([...bankAccounts, {
      id: newId, 
      account: '',
      currency: defaultCurrency,
      swift: getSwiftCodeForBank(NOT_SPECIFIED)
    }]);
    setError(undefined);
  };

  const handleRemoveAccount = (id) => {
    const updatedAccounts = bankAccounts.filter((acc) => acc.id !== id);
    setBankAccounts(updatedAccounts);
  };

  const handleCurrencyChange = (id, newCurrency) => {
    const updatedAccounts = bankAccounts.map((acc) =>
      acc.id === id ? { ...acc, currency: newCurrency } : acc
    );
    setBankAccounts(updatedAccounts);
    setError(undefined);
  };

  const handleAccountChange = (id, newValue) => {
    const isValid = isValidIBAN(electronicFormatIBAN(newValue));

    // Check for duplicate IBAN BEFORE updating state
    if (isValid && newValue.length > 0 && isIbanAlreadyExists(newValue, id)) {
      // Clear the field and show error
      const updatedAccounts = bankAccounts.map((account) =>
        account.id === id ? { ...account, account: '' } : account
      );
      setBankAccounts(updatedAccounts);
      setError(t('alerts:error_client_bank_account_iban_already_exists'));
      return;
    }

    // Update the account normally
    const updatedAccounts = bankAccounts.map((account) =>
      account.id === id ? { ...account, account: newValue } : account
    );
    setBankAccounts(updatedAccounts);

    // Clear any existing error first, then check for new errors
    setError(undefined);
    
    // Check for format errors only after clearing previous errors
    if (!isValid && newValue.length !== 0) {
      setError(t('alerts:error_client_bank_account_type'));
    }
  };

  // Initialize with one empty account if none exist
  useEffect(() => {
    if (bankAccounts.length === 0) {
      setBankAccounts([{
        id: Date.now(),
        account: '',
        currency: 'PLN',
        swift: getSwiftCodeForBank(NOT_SPECIFIED)
      }]);
    }
  }, []);

  return (
    <div className='w-full space-y-6'>
      {bankAccounts && bankAccounts.map((account, index) => (
        <div key={account.id} className='w-full'>
          <div className='w-full space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-start'>
              <div className="md:col-span-2">
                <InputWithAlert
                  value={account.account}
                  onChange={(e) => handleAccountChange(account.id, e.target.value)}
                  label={t('signup:bank_account') + (index === 0 ? '*' : '')}
                  error={inputChecked && (!account.account || !isValidIBAN(electronicFormatIBAN(account.account)))}
                  errorMessage={!account.account ? t('signup:empty_data_invalid') : t('signup:iban_invalid')}
                  placeholder={t('signup:enter_iban')}
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              </div>
              <div className="md:col-span-1 pt-6">
                <div className="block md:hidden">
                  <label className="block text-sm font-medium text-gray-700 pb-1">{t('common:currency') || 'Currency'}</label>
                </div>
                <SelectWithAlert
                  value={account.currency}
                  options={SUPPORTED_CURRENCIES}
                  onChangeValue={(e) => handleCurrencyChange(account.id, e)}
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              </div>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <Button 
                  type="button" 
                  className="bg-inherit hover:bg-inherit shadow-none text-red-500 hover:text-red-700" 
                  onClick={() => handleRemoveAccount(account.id)}
                >
                  <DeleteIcon />
                  <span className="ml-2 md:hidden">{t('common:remove')}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
      
      <Button type="button" className="mt-4" onClick={handleAddAccount}>
        {t('clients:create_client_create_payment_account_button')}
      </Button>
      
      {error && (
        <div className="mt-4">
          <Alert variant="destructive" className="relative mb-2 h-12 flex items-center justify-start !pl-[0.75rem]">
            <AlertCircle className="h-4 w-4 !static !mr-2" />
            <AlertTitle className="m-0 !p-0">
              {error}
            </AlertTitle>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default SignupBankAccount;